import React, { useState, useRef } from "react";
import BulkCsvModal from "./BulkCsvModal";
import { ReactComponent as UploadCloudIcon } from "Assets/svg/bulkCsvConnect/uploadCloud.svg";
import { ReactComponent as CheckCircleIcon } from "Assets/svg/bulkCsvConnect/checkCircle.svg";
import { ReactComponent as DownloadIcon } from "Assets/svg/bulkCsvConnect/download.svg";
import {
  parseCsvText,
  validateCsvFile,
  validateRowCount,
  downloadSampleTemplate
} from "./csvUtils";
import { CSV_MAX_ROWS } from "./constants";

const StepUpload = (props = {}) => {
  const { onClose = () => {}, onBack = () => {}, onNext = () => {} } =
    props || {};
  const [dragging, setDragging] = useState(false);
  const [file, setFile] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const inputRef = useRef();

  const readAndValidateFile = selectedFile => {
    const fileErr = validateCsvFile(selectedFile);
    if (fileErr) {
      setErrorMsg(fileErr);
      setFile(null);
      return;
    }
    const reader = new FileReader();
    reader.onload = event => {
      const text = event?.target?.result || "";
      const { headers, rows } = parseCsvText(text);
      const rowErr = validateRowCount(rows.length);
      if (!headers.length || rowErr) {
        setErrorMsg(rowErr || "Could not read this CSV file.");
        setFile(null);
        return;
      }
      setErrorMsg("");
      setFile(selectedFile);
    };
    reader.onerror = () => {
      setErrorMsg("Could not read this CSV file.");
      setFile(null);
    };
    reader.readAsText(selectedFile);
  };

  const handleDrop = event => {
    event.preventDefault();
    setDragging(false);
    const droppedFile = event.dataTransfer?.files?.[0];
    if (droppedFile) {
      readAndValidateFile(droppedFile);
    }
  };

  const handleContinue = () => {
    if (!file) {
      setErrorMsg("Please choose a file to upload.");
      return;
    }
    const reader = new FileReader();
    reader.onload = event => {
      const text = event?.target?.result || "";
      onNext(text, file.name);
    };
    reader.readAsText(file);
  };

  return (
    <BulkCsvModal
      title="Bulk import via CSV"
      subtitle="Upload a CSV file with your inbox credentials"
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            id="bulkCsvUploadBack"
            className="bulkCsvBtnGhost"
            onClick={onBack}
          >
            Back
          </button>
          <button
            type="button"
            id="bulkCsvUploadContinue"
            className="bulkCsvBtnPrimary"
            disabled={!file}
            onClick={handleContinue}
          >
            Continue
          </button>
        </>
      }
    >
      {errorMsg && (
        <div className="bulkCsvErrorBox">
          <span className="errorText">{errorMsg}</span>
        </div>
      )}

      <div
        className={`bulkCsvDropZone ${dragging ? "dragging" : ""} ${
          file ? "hasFile" : ""
        }`}
        onDragOver={event => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        onClick={() => inputRef.current && inputRef.current.click()}
      >
        <input
          ref={inputRef}
          id="bulkCsvFileInput"
          type="file"
          accept=".csv"
          onChange={event => readAndValidateFile(event.target.files?.[0])}
        />
        <div className="dropZoneIcon">
          {file ? <CheckCircleIcon /> : <UploadCloudIcon />}
        </div>
        <div className="dropZoneLabel">
          {file ? (
            file.name
          ) : (
            <>
              <span className="browseLink">Browse files</span> or drag and drop
            </>
          )}
        </div>
        <div className="dropZoneMeta">
          {file
            ? `${(file.size / 1024).toFixed(1)} KB`
            : `.csv only · max ${CSV_MAX_ROWS} rows`}
        </div>
      </div>

      <div className="bulkCsvTemplateRow">
        <span className="templateText">
          Don't have a CSV? Download our sample template.
        </span>
        <span
          className="templateLink"
          id="downloadBulkCsvTemplate"
          onClick={downloadSampleTemplate}
        >
          <DownloadIcon />
          Download template
        </span>
      </div>

      <div className="bulkCsvTemplateNote">
        If you have just one password for your account, use it for both the
        "IMAP Password" and "SMTP Password" fields. If you're using an app
        password (like with Google), enter that instead of your regular
        password.
      </div>

      <div className="bulkCsvWarningBox">
        <div className="warningText">
          <strong>Microsoft accounts:</strong> SMTP Authentication must be
          enabled before connecting. If you're on Microsoft 365, an admin
          needs to enable this in the M365 Admin Center or via PowerShell.
          <br />
          <br />
          <strong>GoDaddy-hosted Microsoft 365</strong> accounts cannot be
          imported via CSV — connect those using <em>Sign in with Microsoft</em>{" "}
          instead.
        </div>
      </div>
    </BulkCsvModal>
  );
};

export default StepUpload;
export { StepUpload };
